import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Typography,
} from '@kar-dmp/ignite';
import { Icon } from '@Backlot-Cars/archie';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

const EMPTY_CREDIT_ANNOUNCEMENTS = [];

const renderBodyWithBoldMarkdown = (body) =>
	(body ?? '').split(/(\*\*[\s\S]+?\*\*)/g).map((segment, index) => {
		const isBoldMarkdown = segment.startsWith('**') && segment.endsWith('**');

		if (!isBoldMarkdown) return segment;

		return (
			<strong key={index} className="font-semibold">
				{segment.slice(2, -2)}
			</strong>
		);
	});

export const CreditAnnouncementModal = ({ creditAnnouncements: providedCreditAnnouncements, onAnnouncementShown }) => {
	const creditAnnouncements = providedCreditAnnouncements ?? EMPTY_CREDIT_ANNOUNCEMENTS;
	const [isModalOpen, setIsModalOpen] = useState(true);
	const creditAnnouncementToDisplay = creditAnnouncements[0];
	const acknowledgedCreditIds = useRef(new Set());

	useEffect(() => {
		const customerSelfServeCreditId = creditAnnouncementToDisplay?.customer_self_serve_credit?.id;
		if (
			!isModalOpen ||
			!customerSelfServeCreditId ||
			acknowledgedCreditIds.current.has(customerSelfServeCreditId)
		) {
			return;
		}

		acknowledgedCreditIds.current.add(customerSelfServeCreditId);
		onAnnouncementShown?.(customerSelfServeCreditId);
	}, [creditAnnouncementToDisplay, isModalOpen, onAnnouncementShown]);

	if (!creditAnnouncementToDisplay) return null;

	const { amount, body, button, credit_balance_message, footer, header, reason } = creditAnnouncementToDisplay;

	return (
		<Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
			<DialogContent
				aria-atomic="true"
				aria-live="polite"
				className="flex h-[min(597px,calc(100dvh-2rem))] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-y-auto rounded-[19px] bg-white p-0 text-center sm:max-w-md [&_[data-slot=dialog-close]]:top-7 [&_[data-slot=dialog-close]]:right-7 [&_[data-slot=dialog-close]]:z-20 [&_[data-slot=dialog-close]]:text-white"
			>
				<div className="relative flex h-[343px] shrink-0 flex-col items-center overflow-hidden bg-gradient-to-b from-inverted-background-alt to-chart-4">
					<DialogHeader className="z-10 w-full items-center px-8 pt-[63px] sm:px-10">
						<DialogTitle className="w-full pb-0">
							<Typography
								as="span"
								className="mx-auto block max-w-[328px] bg-gradient-to-b from-white to-[#80B3FF] bg-clip-text text-[42px] leading-none font-semibold tracking-[-0.01em] text-transparent"
							>
								{header}
							</Typography>
						</DialogTitle>
					</DialogHeader>
					<div className="absolute inset-x-8 bottom-0 flex h-[160px] items-center justify-center rounded-t-[12px] bg-inverted-background-alt sm:inset-x-[61px]">
						<div className="flex size-[94px] translate-y-3.5 items-center justify-center rounded-full bg-chart-3">
							<Icon name="discount" color="invert" decorative size="largest" />
						</div>
					</div>
				</div>
				<div className="flex min-h-0 flex-1 flex-col items-center px-8 pt-[23px] pb-[19px] sm:px-[53px]">
					<Typography
						as="p"
						className="mx-auto flex h-[57px] w-full max-w-[240px] shrink-0 items-center justify-center text-center text-[30px] leading-[1.75] font-semibold tracking-[-0.01em] text-[#0061FF]"
					>
						{amount}
					</Typography>
					<DialogDescription className="mt-[9px] max-w-[310px] text-[18px] leading-[2] text-[#292929]">
						{renderBodyWithBoldMarkdown(body)}
					</DialogDescription>
					{reason && (
						<Typography as="p" className="mt-0 text-[18px] leading-[2]">
							{renderBodyWithBoldMarkdown(reason)}
						</Typography>
					)}
					{credit_balance_message && (
						<Typography as="p" className="mt-0 text-[18px] leading-[2]">
							{credit_balance_message}
						</Typography>
					)}
					<DialogFooter className="mt-5 w-full flex-col sm:flex-col">
						<Button
							className="h-[36px] rounded-[2px] text-[16px] leading-none font-medium"
							fullWidth
							nativeButton={false}
							render={<Link href="/search" />}
						>
							{button}
						</Button>
					</DialogFooter>
					<Typography as="p" className="mt-[5px] text-xs" color="muted" variant="small">
						{footer}
					</Typography>
				</div>
			</DialogContent>
		</Dialog>
	);
};
